import React, { useEffect, useState } from "react";
import "./Header.css";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import img from "./images/logo.webp";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoClose } from "react-icons/io5";
import { FiHeart, FiSearch, FiShoppingCart, FiUser } from "react-icons/fi";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FormModal from "../PopupModal/FormModal";
import axiosInstance from "../utils/axiosInstance";
import LoginModal from "./Login/Login";
import { clearWishlist, setWishlistItems, setWishlistTotalCount } from "../redux/wishlistSlice";

const SEARCH_DEBOUNCE_MS = 250;

function Header({ scrollToProduct, scrollToAbout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [siteData, setSiteData] = useState({
    logoUrl: localStorage.getItem("siteLogo") || "",
    companyName: "",
    title: ""
  });

  const [profileName, setProfileName] = useState(localStorage.getItem("name") || "");
  const [profileMenuHidden, setProfileMenuHidden] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let stopScrollTimer = null;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 50) {
        setIsHeaderHidden(false);
        lastScrollY = currentScrollY;
        return;
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderHidden(true);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderHidden(false);
      }

      lastScrollY = currentScrollY;

      if (stopScrollTimer) clearTimeout(stopScrollTimer);
      stopScrollTimer = setTimeout(() => {
        setIsHeaderHidden(false);
      }, 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (stopScrollTimer) clearTimeout(stopScrollTimer);
    };
  }, []);

  const handleProfileMenuNav = (path) => {
    document.activeElement?.blur();
    setProfileMenuHidden(true);
    navigate(path);
  };

  const { buyCart = [], rentCart = [] } = useSelector((store) => store.cart || {});
  const wishlistTotalCount = useSelector((store) => store.wishlist?.totalCount);
  const wishlistItems = useSelector((store) => store.wishlist?.items || []);
  const cartCount = buyCart.length + rentCart.length;
  // Use server total count when available, otherwise fall back to Redux items length
  const wishlistCount = wishlistTotalCount !== null && wishlistTotalCount !== undefined
    ? wishlistTotalCount
    : wishlistItems.length;

  /* Helper: fetch full wishlist from server and store count + items in Redux */
  const fetchWishlistCount = React.useCallback(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");
    if (!token || !userId) return;

    axiosInstance
      .get("catlog/wishlists/", { params: { user: userId, is_suspended: false, limit: 200 } })
      .then((res) => {
        const serverItems = Array.isArray(res.data?.results) ? res.data.results : (Array.isArray(res.data) ? res.data : []);
        const count = typeof res.data?.count === "number" ? res.data.count : serverItems.length;

        // Set total count badge from response
        dispatch(setWishlistTotalCount(count));

        /* Map server wishlist items into the shape the Redux slice expects */
        const mapped = serverItems.map((entry) => ({
          id: entry.varient?.id || entry.varient_id || entry.variant_id,
          wishlistId: entry.id,
          name: entry.varient?.name || entry.varient_name || entry.name || "",
          type: entry.type === "rental" ? "rent" : "buy",
        }));
        dispatch(setWishlistItems(mapped));
      })
      .catch(() => { });
  }, [dispatch]);

  /* Sync wishlist from server on mount for logged-in users */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");
    if (!token || !userId) return;

    // Fetch full items list for local state seeding
    axiosInstance
      .get("catlog/wishlists/", { params: { user: userId, is_suspended: false, limit: 200 } })
      .then((res) => {
        const serverItems = Array.isArray(res.data?.results) ? res.data.results : (Array.isArray(res.data) ? res.data : []);
        const count = typeof res.data?.count === "number" ? res.data.count : serverItems.length;

        // Set total count badge from response
        dispatch(setWishlistTotalCount(count));

        /* Map server wishlist items into the shape the Redux slice expects */
        const mapped = serverItems.map((entry) => ({
          id: entry.varient?.id || entry.varient_id || entry.variant_id,
          wishlistId: entry.id,
          name: entry.varient?.name || entry.varient_name || entry.name || "",
          type: entry.type === "rental" ? "rent" : "buy",
        }));
        dispatch(setWishlistItems(mapped));
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Refresh wishlist count & items whenever any component adds/removes an item. */
  useEffect(() => {
    const handleWishlistUpdated = () => {
      fetchWishlistCount();
    };
    window.addEventListener("wishlistUpdated", handleWishlistUpdated);
    return () => window.removeEventListener("wishlistUpdated", handleWishlistUpdated);
  }, [fetchWishlistCount]);

  const isHomeActive = location.pathname === "/";
  const isSaleActive =
    location.pathname === "/buy" || location.pathname.startsWith("/buy/");
  const isRentActive =
    location.pathname === "/rent" || location.pathname.startsWith("/rent/");

  useEffect(() => {
    setCatalogSearch(searchParams.get("q") || searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    const handleAuthChange = () => {
      setProfileName(localStorage.getItem("name") || "");
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  useEffect(() => {
    const searchText = catalogSearch.trim();

    if (searchText.length < 2) {
      setSuggestions([]);
      return undefined;
    }

    const debounceTimer = setTimeout(() => {
      axiosInstance
        .get("catlog/search-keywords/", {
          params: { search: searchText },
        })
        .then((res) => {
          const nextSuggestions = Array.isArray(res.data?.suggestions)
            ? res.data.suggestions
            : [];
          setSuggestions(nextSuggestions);
        })
        .catch(() => {
          setSuggestions([]);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer);
  }, [catalogSearch]);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        let res;
        try {
          res = await axiosInstance.get("sitedata/site-config/");
        } catch (err) {
          console.warn("site-config failed, falling back to site API", err);
          res = await axiosInstance.get("sitedata/site/");
        }

        const site = res?.data?.site || res?.data?.results?.[0];
        const theme = res?.data?.theme;
        const tenant = res?.data?.tenant;

        if (!site) return;

        const logoUrl = theme?.logo_url || "";
        if (logoUrl) {
          localStorage.setItem("siteLogo", logoUrl);
        }

        setSiteData({
          ...site,
          logoUrl: logoUrl || localStorage.getItem("siteLogo") || "",
          companyName: site.title || tenant?.company_name || ""
        });

        if (site.title || tenant?.company_name) {
          document.title = site.title || tenant?.company_name;
        }

        const favicon = theme?.favicon_url;


        if (favicon) {
          let link = document.querySelector("link[rel*='icon']");

          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }

          link.href = favicon;
        }
      } catch (error) {
        // console.error(error);
      }
    };
    fetchSiteData();
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await axiosInstance.get("stores/stores/", {
          params: { page_size: 1000, is_suspended: false }
        });
        console.log("Stores fetched:", res.data);
        if (res.data?.results?.length > 0) {
          const store = res.data.results[0];
          if (!localStorage.getItem("store_id")) {
            localStorage.setItem("store_id", store.id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch stores:", error);
      }
    };

    fetchStores(); // Initial call
    const intervalId = setInterval(fetchStores, 5000); // Polling every 5 seconds

    return () => clearInterval(intervalId); // Cleanup
  }, []);

  const closeMenu = () => setIsOpen(false);

  const handleAboutClick = (event) => {
    closeMenu();
    event.preventDefault();
    navigate("/");
    setTimeout(() => scrollToAbout?.(), 0);
  };

  const handleProductsClick = (event) => {
    closeMenu();
    event.preventDefault();
    navigate("/");
    setTimeout(() => scrollToProduct?.(), 0);
  };

  const handleHomeClick = (event) => {
    closeMenu();
    if (event && event.preventDefault) event.preventDefault();
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCatalogSearch = (event) => {
    event.preventDefault();
    const nextSearch = catalogSearch.trim();
    setShowSuggestions(false);
    const params = new URLSearchParams();
    params.set("store_id", localStorage.getItem("store_id"));

    if (nextSearch) {
      params.set("q", nextSearch);
    }

    navigate(`/search/results?${params.toString()}`);
  };

  const searchForSuggestion = (suggestion) => {
    const params = new URLSearchParams();
    params.set("store_id", localStorage.getItem("store_id"));
    params.set("q", suggestion);
    setCatalogSearch(suggestion);
    setShowSuggestions(false);
    navigate(`/search/results?${params.toString()}`);
  };

  const handleLoginSuccess = (data) => {
    setProfileName(data?.name || localStorage.getItem("name") || "Customer");
    setShowLogin(false);
    // Refresh count after login
    fetchWishlistCount();
  };

  const handleLogout = () => {
    document.activeElement?.blur();
    setProfileMenuHidden(true);
    ["token", "userid", "name", "store_id", "defaultAddress", "defaultAddressId", "saleAddress", "rentalAddress", "verifiedZipcode", "savedZipcode"].forEach((key) => localStorage.removeItem(key));
    setProfileName("");
    dispatch(clearWishlist());
    dispatch(setWishlistTotalCount(0));
    window.dispatchEvent(new Event("authChange"));
    closeMenu();
    toast.success("Logged out");
    navigate("/");
  };

  const profileInitial = (profileName || "Customer").charAt(0).toUpperCase();

  return (
    <div className={`header-shell${isHeaderHidden ? " header-hidden" : ""}`}>
      <header className="header">
        <div className="header-mobile-left">
          <div className="icon-container" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? (
              <IoClose className="react-icon" />
            ) : (
              <GiHamburgerMenu className="react-icon" />
            )}
          </div>
          <div onClick={handleHomeClick} className="logo-wrapper cursor-pointer">
            {siteData?.logoUrl ? (
              <img
                src={siteData.logoUrl}
                className="header-img"
                alt={siteData?.companyName || siteData?.title || "Logo"}
              />
            ) : (
              <h2 className="site-title">
                {siteData?.companyName || siteData?.title || "Site Exit"}
              </h2>
            )}
          </div>
        </div>

        <nav className={`nav ${isOpen ? "active" : "close"}`}>
          {/* Overlay backdrop — clicking it closes the menu on mobile */}
          <div className="nav-backdrop" onClick={closeMenu} />

          <div className="nav-links">
            <Link
              onClick={handleHomeClick}
              to="/"
              className={isHomeActive ? "nav-mode-active" : ""}
            >
              Home
            </Link>
            <Link
              onClick={closeMenu}
              to="/buy"
              className={isSaleActive ? "nav-mode-active" : ""}
            >
              Sale
            </Link>
            <Link
              onClick={closeMenu}
              to="/rent"
              className={isRentActive ? "nav-mode-active" : ""}
            >
              Rent
            </Link>
          </div>
        </nav>

        <form className="header-search" onSubmit={handleCatalogSearch}>
          <input
            type="search"
            value={catalogSearch}
            placeholder="What are you searching for?"
            onChange={(event) => setCatalogSearch(event.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
          />
          <button className="header-search-submit" type="submit" aria-label="Search">
            <FiSearch />
          </button>
          {showSuggestions && suggestions.length > 0 && (
            <div className="header-search-suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="header-search-suggestion"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => searchForSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </form>

        <div className="right-container">
          <div className="header-actions">
            <div className="header-profile-wrap" onMouseEnter={() => setProfileMenuHidden(false)}>
              <button
                type="button"
                className={`header-icon-btn header-profile-btn${profileName ? " is-logged-in" : ""}`}
                onClick={() => {
                  if (!profileName) setShowLogin(true);
                }}
                aria-label={profileName ? `Profile: ${profileName}` : "Login"}
                title={profileName || "Login"}
              >
                {profileName ? <span>{profileInitial}</span> : <FiUser />}
              </button>
              <div className="header-profile-menu" style={profileMenuHidden ? { opacity: 0, visibility: "hidden", pointerEvents: "none" } : {}}>
                {profileName ? (
                  <>
                    <p>Hello <strong>{profileName}</strong></p>
                    <button type="button" onClick={() => handleProfileMenuNav("/orders")}>My orders</button>
                    <button type="button" onClick={() => handleProfileMenuNav("/address")}>My address</button>
                    <button type="button" onClick={() => handleProfileMenuNav("/wishlist")}>Wishlist</button>
                    <button type="button" className="header-logout-btn" onClick={handleLogout}>Logout</button>
                  </>
                ) : (
                  <>
                    <p>Hello <strong>User</strong></p>
                    <button type="button" onClick={() => {
                      document.activeElement?.blur();
                      setProfileMenuHidden(true);
                      setShowLogin(true);
                    }}>Login</button>
                  </>
                )}
              </div>
            </div>

            <button
              type="button"
              className={`header-icon-btn${location.pathname === "/wishlist" ? " active" : ""}`}
              onClick={() => {
                closeMenu();
                if (!profileName) {
                  setShowLogin(true);
                } else {
                  navigate("/wishlist");
                }
              }}
              aria-label="Wishlist"
            >
              <FiHeart />
              {wishlistCount > 0 && <span className="header-badge">{wishlistCount}</span>}
            </button>

            <button
              type="button"
              className={`header-icon-btn${location.pathname === "/cart" ? " active" : ""}`}
              onClick={() => {
                closeMenu();
                navigate("/cart");
              }}
              aria-label="Cart"
            >
              <FiShoppingCart />
              {cartCount > 0 && <span className="header-badge">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      <FormModal show={show} setShow={setShow} />
      <LoginModal show={showLogin} onHide={() => setShowLogin(false)} onLoginSuccess={handleLoginSuccess} />
    </div>
  );
}

export default Header;